import { Alert } from '@creditornot/cb-alert';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useRouteMatch } from 'react-router-dom';
import styled from 'styled-components';

import { Campaign, CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';

import { CampaignsQueryKeys } from '../../../campaignsQueryKeys';
import { useCampaignModuleContext } from '../../../context';
import { useCampaignConfiguration } from '../../../hooks/useCampaignConfiguration';
import { useCampaignMutations } from '../../../hooks/useCampaignMutations';
import { useCampaignsRoutes } from '../../../hooks/useCampaignsRoutes';
import { CampaignAction, CampaignsRoutesParams } from '../../../types';
import { getBasketDiscountType } from '../../../utils/getBasketDiscountType';
import { getCampaignActions } from '../../../utils/getCampaignActions';
import { getCampaignStatus } from '../../../utils/getCampaignStatus';
import { AlertsBanner } from '../../AlertsBanner';
import BasketDiscountDetails, { BasketDiscountDetailsProps } from '../BasketDiscountDetails/BasketDiscountDetails';
import DeliveryDiscountDetails, {
  DeliveryDiscountDetailsProps,
} from '../DeliveryDiscountDetails/DeliveryDiscountDetails';
import EndOfDayCampaignDetails, {
  EndOfDayCampaignDetailsProps,
} from '../EndOfDayCampaignDetails/EndOfDayCampaignDetails';
import ItemDiscountDetails, { ItemDiscountDetailsProps } from '../ItemDiscountDetails/ItemDiscountDetails';

type PromoCampaignDetailsProps = {
  campaignTypesPermittedToEdit: CampaignType[];
  campaign: Campaign;
  venueDetails: Pick<Venue, 'name' | 'short_description' | 'listimage'> | undefined;
  menuId?: string;
};

const StyledAlert = styled(Alert)`
  margin-block-start: 32px;
  margin-inline: 16px;
`;

export const PromoCampaignDetails = ({
  campaignTypesPermittedToEdit,
  campaign,
  venueDetails,
  menuId,
}: PromoCampaignDetailsProps) => {
  const { entityType, country, venues, merchantId, timezone } = useCampaignModuleContext();
  const venueIds = venues.map(venue => venue.id);

  const { params } = useRouteMatch<CampaignsRoutesParams>();
  const campaignsRoutes = useCampaignsRoutes();
  const history = useHistory();
  const { deleteCampaign, terminate } = useCampaignMutations({ merchantId, venueIds });
  const queryClient = useQueryClient();

  const {
    configurationQuery: { isLoading: isConfigurationLoading },
    campaignTypesAvailableToEdit,
  } = useCampaignConfiguration({ merchantId, venueIds, campaignTypesPermittedToEdit });

  const isLoading = isConfigurationLoading || deleteCampaign.isLoading || terminate.isLoading;

  const campaignStatus = getCampaignStatus(campaign); // TODO: @adtech revert back after proper transormation

  const handleClone = () => history.push(`${campaignsRoutes.new}?campaignId=${campaign?.id}`);
  const handleEdit = (campaignId: string) => history.push(campaignsRoutes.edit(campaignId));
  const handleDelete = (campaignId: string) =>
    deleteCampaign.mutate({ merchantId, campaignId }, { onSuccess: () => history.push(campaignsRoutes.index) });
  const handleTerminate = (campaignId: string) =>
    terminate.mutate(
      { merchantId, campaignId },
      { onSuccess: () => queryClient.invalidateQueries(CampaignsQueryKeys.GET_SINGLE(merchantId, params.campaignId)) },
    );

  const availableActions: CampaignAction[] | undefined =
    campaignStatus &&
    getCampaignActions({
      campaignType: campaign.campaign_type,
      campaignStatus,
      country,
      isCreatedByWolt: campaign.created_by_wolt,
      isMerchantLevel: entityType === 'merchant',
      venueIds: campaign.venue_ids,
    });

  const banner = <AlertsBanner campaignStatus={campaignStatus} campaign={campaign} />;

  const commonProps:
    | DeliveryDiscountDetailsProps
    | BasketDiscountDetailsProps
    | ItemDiscountDetailsProps
    | EndOfDayCampaignDetailsProps = {
    availableActions,
    banner,
    campaign,
    campaignTypesAvailableToEdit,
    country,
    isLoading,
    timezone,
    venueDetails,
    onClone: handleClone,
    onDelete: handleDelete,
    onEdit: handleEdit,
    onTerminate: handleTerminate,
  };

  return (
    <>
      {campaign.campaign_type === CampaignType.DELIVERY_DISCOUNT && <DeliveryDiscountDetails {...commonProps} />}

      {campaign.campaign_type === CampaignType.BASKET_DISCOUNT && (
        <BasketDiscountDetails discountType={getBasketDiscountType(campaign.basket_discount)} {...commonProps} />
      )}

      {campaign.campaign_type === CampaignType.ITEM_DISCOUNT && (
        <>
          {menuId ? (
            <ItemDiscountDetails menuId={menuId} {...commonProps} />
          ) : (
            <StyledAlert variant="error">
              <FormattedMessage id="campaigns.menu-unavailable" />
            </StyledAlert>
          )}
        </>
      )}

      {campaign.campaign_type === CampaignType.END_OF_DAY && (
        <>
          {menuId ? (
            <EndOfDayCampaignDetails menuId={menuId} {...commonProps} />
          ) : (
            <StyledAlert variant="error">
              <FormattedMessage id="campaigns.menu-unavailable" />
            </StyledAlert>
          )}
        </>
      )}
    </>
  );
};
